import React from 'react';

import { BuilderBlocks } from '@builder.io/react';
import config from 'config';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import useHasFeature, { featureNames } from '~/shared/utils/useHasFeature';
import useSecondarySite from '~/shared/utils/useSecondarySite';
import { mobile } from '~/techstyle-shared/react-components';
import { useDomain } from '~/techstyle-shared/redux-core';

import MetaNavItem from './MetaNavItem';

const MetaNavContainer = styled.div`
  ${({ theme }) => theme.builderHeader.metaNav};
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  ${mobile`
    position: relative;
    z-index: 15;
  `}
`;

const MetaNavBrands = styled.ul`
  ${({ theme }) => theme.lists.bare};
  display: flex;
  flex: 50%;
  list-style: none;
  align-items: center;
  z-index: 1;
  position: relative;
  height: 32px;
  ${mobile`
    flex: 1;
    height: 40px;
  `}
`;

const MetaNavOptions = styled.ul`
  ${({ theme }) => theme.lists.bare};
  display: flex;
  flex: 50%;
  list-style: none;
  align-items: center;
  justify-content: flex-end;
  padding-right: 30px;
  .builder-block {
    display: flex;
    align-items: center;
  }
  ${mobile`
    display: none;
  `}
`;

const BuilderMetaNav = ({ builderBlock }) => {
  const metaNavItems = config.get(
    'public.brand.navigation.builderMetaNav.links'
  );
  const { tld } = useDomain();
  const { enabled: isOutletEnabled } = useHasFeature(
    featureNames.WAREHOUSE_OUTLET_SITE
  );
  const displayItems = metaNavItems.filter(
    (item) =>
      !item.exclusiveDomainList || item.exclusiveDomainList.includes(tld)
  );
  const secondarySite = useSecondarySite();
  const activeBrandName = config.get('public.brand.name');
  const filteredItems = displayItems.filter((item) => {
    if (item.brand === 'fabletics' && item.isSecondarySite) {
      return isOutletEnabled;
    }
    return true;
  });

  return (
    <MetaNavContainer data-builder-component-type="metanav">
      <MetaNavBrands>
        {filteredItems.map((item, index) => {
          const isActiveBrand = secondarySite
            ? activeBrandName === item.brand &&
              `/${secondarySite}` === item.href
            : activeBrandName === item.brand && !item.isSecondarySite;
          // Hard reload necessary when switching between secondary sites as it's a separate application
          if (
            secondarySite &&
            activeBrandName === item.brand &&
            !item.isSecondarySite
          ) {
            const url = new URL(item.href, window.location.origin);
            const params = new URLSearchParams(url.search);
            params.set(
              'refresh',
              params.has('refresh') ? params.get('refresh') : '1'
            );
            params.set(
              'destination',
              params.has('destination') ? params.get('destination') : item.href
            );
            url.search = params.toString();
            item.href = url.pathname + url.search;
          }
          return (
            <MetaNavItem
              key={index}
              item={item}
              isActiveBrand={isActiveBrand}
            />
          );
        })}
      </MetaNavBrands>
      {builderBlock && (
        <MetaNavOptions>
          <BuilderBlocks
            blocks={builderBlock.children}
            parentElementId={builderBlock.id}
          />
        </MetaNavOptions>
      )}
    </MetaNavContainer>
  );
};

BuilderMetaNav.propTypes = {
  builderBlock: PropTypes.object,
};

export default BuilderMetaNav;
